import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { CommonContentContext } from '@/utils/context/commonContent/CommonContentContext';
import Weather from '../weather/Weather';
import { useList } from '@refinedev/core';
import { SprayRecordRecordStatus } from '@/utils/sprayRecord/enums/SprayRecordRecordStatus.enum';
import { SprayRecordSearchBy } from '@/utils/sprayRecord/enums/SprayRecordSearchBy.enum';
import RoleGuard from '../common/RoleGuard';
import { UserRole } from '@/utils/interfaces/common/UserRole.enum';
import { ActivityCalendar } from './ActivityCalendar/ActivityCalendar';

export const Content = () => {
  const [taskOverviewDropDownOpen, setTaskOverviewDropDownOpen] = useState(false);
  const [taskOverviewDropDownOption, setTaskOverviewDropDownOption] = useState('This week');
  const [farmCount, setFarmCount] = useState(0);
  const [sprayRecordPlannedTotal, setSprayRecordPlannedTotal] = useState(0);
  const [sprayRecordCompletedTotal, setSprayRecordCompletedTotal] = useState(0);

  const [jobOpen, setJobOpen] = useState(true);
  const [itemJobOpen, setItemJobOpen] = useState(true);
  const router = useRouter();

  const { data: farmsData } = useList({ resource: 'Farms' });
  const { data: sprayRecordsCompleted } = useList({
    resource: 'SprayRecords',
    filters: [
      {
        field: 'search',
        operator: 'eq',
        value: SprayRecordRecordStatus[SprayRecordRecordStatus.Completed],
      },
      {
        field: 'searchBy',
        operator: 'eq',
        value: SprayRecordSearchBy.General,
      },
    ],
  });
  const { data: sprayRecordsPlanned } = useList({
    resource: 'SprayRecords',
    filters: [
      {
        field: 'search',
        operator: 'eq',
        value: SprayRecordRecordStatus[SprayRecordRecordStatus.Planned],
      },
      {
        field: 'searchBy',
        operator: 'eq',
        value: SprayRecordSearchBy.General,
      },
    ],
  });
  useEffect(() => {
    const farmTotal = farmsData?.total;
    const recordPlannedTotal = sprayRecordsPlanned?.total;
    const recordCompletedTotal = sprayRecordsCompleted?.total;

    setFarmCount(farmTotal ?? 0);
    setSprayRecordPlannedTotal(recordPlannedTotal ?? 0);
    setSprayRecordCompletedTotal(recordCompletedTotal ?? 0);
  }, [farmsData, sprayRecordsPlanned, sprayRecordsCompleted]);

  return (
    <div className="flex justify-center">
      <div className="flex w-full min-w-0 flex-col gap-5">
        <div className="infoSection flex w-full max-w-[290px] flex-wrap justify-start gap-[45px] pl-[20px] pr-[20px] min-[588px]:max-w-[585px] min-[880px]:max-w-none">
          <div className="flex h-[160px] w-[250px] flex-col justify-evenly rounded-[10px] bg-primary pl-[18px] text-white">
            <p className="text-[17px] font-bold">Total Farms</p>
            <div>
              <p className="text-[45px] font-bold">{farmCount}</p>
            </div>
          </div>

          <div className="flex h-[160px] w-[250px] flex-col justify-evenly rounded-[10px] bg-primary pl-[18px] text-white">
            <p className="text-[17px] font-bold">Spray Records</p>
            <div>
              <p className="text-[20px] font-bold">Planned: {sprayRecordPlannedTotal}</p>
              <p className="text-[20px] font-bold">Completed: {sprayRecordCompletedTotal} </p>
            </div>
          </div>

          <Weather />
        </div>

        <div className="px-[20px]">
          <ActivityCalendar />
        </div>

        <div className="flex justify-center gap-[120px] pb-[80px] pl-[20px] pr-[20px] min-[588px]:justify-start">
          <RoleGuard requiredRoles={[UserRole.Admin, UserRole.FarmManager, UserRole.SuperAdmin]}>
            <div className="enterData flex flex-col gap-[25px]">
              <p className="text-[25px] font-bold text-primary">Enter Data</p>
              <div className="flex flex-col gap-[30px] pl-[10px]">
                <button className="flex items-center gap-[20px]" onClick={() => router.push('/spray-record/create')}>
                  <div className="flex h-[30px] w-[30px] items-center justify-center rounded-[30px] bg-primary">
                    <img className="w-[20px]" src="/icons/add-icon.png" alt="Add icon" />
                  </div>
                  <p className="hover:text-primary-light text-[18px] font-semibold text-primary hover:text-xl">
                    Create Spray Record
                  </p>
                </button>
              </div>
            </div>
          </RoleGuard>

          <div className="hidden">
            <div className="relative inline-block text-left">
              <button
                onClick={() => setTaskOverviewDropDownOpen(!taskOverviewDropDownOpen)}
                className="flex items-end gap-[30px]"
                id="dropdownDefaultButton"
                data-dropdown-toggle="dropdown"
              >
                <p className="text-[25px] font-bold text-[#73cfc8]">Tasks Overview</p>
                <div className="flex h-[35px] w-[97px] items-center justify-center gap-[5px] rounded-[7px] bg-[#e8e5e5] pr-[5px]">
                  <img className="w-[15px]" src="/icons/header-arrow-icon.png" alt="Arrow icon" />
                  <p className="text-[13px]">{taskOverviewDropDownOption}</p>
                </div>
              </button>
              <div
                id="dropdown"
                className={`${
                  taskOverviewDropDownOpen ? 'scale-100 opacity-100' : 'hidden scale-95 opacity-0'
                } absolute right-0 z-10 mt-2 w-56 origin-top-right transform rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition duration-100 ease-out focus:outline-none`}
              >
                <div className="py-1">
                  <div
                    onClick={() => {
                      setTaskOverviewDropDownOpen(false);
                      setTaskOverviewDropDownOption('This week');
                    }}
                    className="block cursor-pointer px-4 py-2 text-sm text-gray-700"
                  >
                    This week
                  </div>
                  <div
                    onClick={() => {
                      setTaskOverviewDropDownOpen(false);
                      setTaskOverviewDropDownOption('This month');
                    }}
                    className="block cursor-pointer px-4 py-2 text-sm text-gray-700"
                  >
                    This month
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-[15px]">
              <button onClick={() => setJobOpen(!jobOpen)} className="flex w-[152px] items-center gap-[15px]">
                <p className="font-bold text-[#5daba5]">Priority Work</p>
                <img
                  className={`${!jobOpen && 'rotate-180'} w-[35px]`}
                  src="/icons/header-arrow-icon.png"
                  alt="Arrow icon"
                />
              </button>

              <div className={`${jobOpen ? 'flex' : 'hidden'} gap-[5px]`}>
                <img className="h-[30px] w-[30px]" src="icons/flag-icon.png" alt="Flag icon" />
                <div
                  onClick={() => setItemJobOpen(!itemJobOpen)}
                  className="flex items-center gap-[45px] border-[4px] border-[#73cfc8] border-[solid] bg-[#9dede7]"
                >
                  <div className="flex flex-col gap-[5px]">
                    <p className="font-medium text-[#4a7e7a]">B27 - full block: Spray</p>
                    <div className={`${!itemJobOpen && 'hidden'} flex flex-col gap-[5px]`}>
                      <p className="font-medium text-[#4a7e7a]">B28 - full block: Spray</p>
                      <p className="font-medium text-[#4a7e7a]">B29 - full block: Spray</p>
                    </div>
                  </div>
                  <div className="h-full">
                    <img
                      className={`${itemJobOpen && 'rotate-180'} mr-[5px] w-[24px]`}
                      src="icons/header-arrow-icon.png"
                      alt="Arrow icon"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden w-[250px] flex-col gap-[25px] rounded-[10px] border-[3px] border-[#73cfc8] border-[solid] pb-[30px] pl-[15px] pr-[15px] pt-[15px]">
            <p className="text-[25px] font-bold text-[#73cfc8]">Pending approvals</p>
            <div className="flex flex-col gap-[25px]">
              <div className="flex h-[35px] items-center justify-center gap-[20px] rounded-[10px] border-[2px] border-[#73cfc8] border-[solid]">
                <p>Work completed</p>
                <p className="text-[#e29200]">18</p>
              </div>
              <div className="flex h-[35px] items-center justify-center gap-[67px] rounded-[10px] border-[2px] border-[#73cfc8] border-[solid]">
                <p>Timesheets</p>
                <p className="text-[#e29200]">4</p>
              </div>
              <div className="flex h-[35px] items-center justify-center gap-[56px] rounded-[10px] border-[2px] border-[#73cfc8] border-[solid]">
                <p>Procurement</p>
                <p className="text-[#e29200]">2</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
